import { Input } from '@/components/ui/input'
import { m } from '@/lib/paraglide/messages'
import { formatVND } from '@/lib/format'

interface Props {
    /** Large summary line: what the courier will collect right now. */
    summaryTotal: string
    /** Breakdown of that total, or the "no freight yet" placeholder. */
    summaryDetail: string
    /** Outstanding goods amount, used for the hint and the below-goods warning. */
    goodsCOD: number
    value: string
    onChange: (raw: string) => void
    /** Set when submit was attempted with an invalid value. */
    error: string | null
    /** Non-blocking hints besides the below-goods one, e.g. the recipient's
     *  freight looking like it was typed in twice. */
    warnings?: string[]
}

/** Parses the override box: '' = use the order's outstanding goods, a number
 *  = the goods amount to collect (freight is added on top separately when
 *  the recipient pays it), anything else (or a negative) = invalid. */
export function parseCODOverride(raw: string): {
    amount?: number
    error: string | null
} {
    const trimmed = raw.trim()
    if (trimmed === '') return { error: null }
    const amount = Number(trimmed)
    if (!Number.isFinite(amount) || amount < 0) {
        return { error: m.carrier_form_cod_override_invalid() }
    }
    return { amount, error: null }
}

export function CarrierCODField({
    summaryTotal,
    summaryDetail,
    goodsCOD,
    value,
    onChange,
    error,
    warnings = [],
}: Props) {
    const parsed = parseCODOverride(value)
    const belowGoods =
        parsed.amount !== undefined && parsed.amount < goodsCOD
            ? m.carrier_form_cod_override_below_goods({
                  goods: formatVND(goodsCOD),
              })
            : null
    const allWarnings = belowGoods ? [belowGoods, ...warnings] : warnings
    return (
        <div className="space-y-2">
            <div className="bg-muted space-y-0.5 rounded-md px-3 py-2">
                <p className="text-lg font-semibold">{summaryTotal}</p>
                <p className="text-muted-foreground text-sm">{summaryDetail}</p>
            </div>
            <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="carrier-cod">
                    {m.carrier_form_cod_override()}
                </label>
                <Input
                    id="carrier-cod"
                    type="number"
                    min={0}
                    step="any"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                    {m.carrier_form_cod_override_hint({
                        goods: formatVND(goodsCOD),
                    })}
                </p>
                {error && (
                    <p className="text-destructive text-sm" role="alert">
                        {error}
                    </p>
                )}
                {!error &&
                    allWarnings.map((warning) => (
                        <p
                            key={warning}
                            className="text-warning text-sm"
                            role="status"
                        >
                            {warning}
                        </p>
                    ))}
            </div>
        </div>
    )
}
